import { Link } from "wouter";
import { ShieldCheck, MessageSquare, BarChart2, Zap, Users } from 'lucide-react';
import PricingModal from "../components/PricingModal";
import { useState } from "react";

const features = [
  {
    name: 'Personalized Coaching',
    description: 'Our AI coaches adapt to your unique goals and communication style, providing truly personalized guidance.',
    icon: MessageSquare,
  },
  {
    name: 'Growth Analytics',
    description: 'Track your progress with our Growth Dashboard, identifying trends and blindspots in your development.',
    icon: BarChart2,
  },
  {
    name: 'Instant & On-Demand',
    description: 'Get coaching support in minutes, whenever you need it. No scheduling, no waiting.',
    icon: Zap,
  },
  {
    name: 'Diverse Coaching Team',
    description: 'Access a team of 6 distinct AI personas, from leadership mentors to empathetic listeners.',
    icon: Users,
  },
];

const LandingPage = () => {
  const [isPricingModalOpen, setIsPricingModalOpen] = useState(false);

  return (
    <div className="bg-white text-slate-800 font-sans">
      {/* Header */}
      <header className="sticky top-0 bg-white/80 backdrop-blur-lg z-10 border-b">
        <div className="container mx-auto px-4 h-20 flex justify-between items-center">
          <Link href="/" className="font-display text-2xl font-bold text-primary">
            HiPo AI Coach
          </Link>
          <nav className="hidden md:flex items-center space-x-8">
            <a href="#features" className="relative text-slate-600 hover:text-primary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded-md after:absolute after:left-0 after:-bottom-1 after:h-0.5 after:w-full after:bg-primary after:scale-x-0 hover:after:scale-x-100 after:origin-left after:transition-transform">Features</a>
            <button 
              onClick={() => setIsPricingModalOpen(true)}
              className="relative text-slate-600 hover:text-primary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded-md after:absolute after:left-0 after:-bottom-1 after:h-0.5 after:w-full after:bg-primary after:scale-x-0 hover:after:scale-x-100 after:origin-left after:transition-transform"
            >
              Pricing
            </button>
          </nav>
          <div className="flex items-center space-x-4">
            <Link href="/login" className="text-slate-600 hover:text-primary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded-md">
              Login
            </Link>
            <Link href="/signup" className="bg-primary text-primary-foreground px-5 py-2.5 rounded-lg font-semibold hover:bg-primary/90 transition-colors transition-transform hover:scale-105">
              Get Started
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <main className="container mx-auto px-4">
        <section className="text-center pt-24 pb-32 rounded-3xl bg-gradient-to-b from-primary/5 to-transparent ring-1 ring-border">
          <h1 className="font-display text-5xl md:text-7xl font-extrabold text-slate-900 leading-tight">
            Your Personal AI Coach for <br /> Professional Growth
          </h1>
          <p className="mt-6 text-lg md:text-xl text-slate-600 max-w-3xl mx-auto">
            Navigate workplace challenges, accelerate your career, and build self-awareness with on-demand, confidential coaching from a team of expert AI personas.
          </p>
          <div className="mt-10 flex justify-center items-center gap-4">
            <Link href="/signup" className="bg-primary text-primary-foreground px-8 py-4 rounded-lg font-semibold text-lg hover:bg-primary/90 transition-transform hover:scale-105">
              Start Your Journey
            </Link>
            <Link href="#features" className="relative text-primary font-semibold px-8 py-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded-md after:absolute after:left-0 after:-bottom-1 after:h-0.5 after:w-full after:bg-primary after:scale-x-0 hover:after:scale-x-100 after:origin-left after:transition-transform">
              Learn More →
            </Link>
          </div>
          <div className="mt-8 flex items-center justify-center text-sm text-slate-500">
            <ShieldCheck className="h-5 w-5 mr-2 text-green-500" />
            <span>Confidential, Secure, and Available 24/7</span>
          </div>
        </section>

        {/* Features Section */}
        <section id="features" className="py-24 bg-slate-50 rounded-2xl">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="font-display text-4xl md:text-5xl font-bold text-slate-900">A Better Way to Grow</h2>
            <p className="mt-4 text-lg text-slate-600">
              HiPo AI Coach isn't just a chatbot. It's a structured ecosystem for professional development.
            </p>
          </div>
          <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature) => (
              <div key={feature.name} className="p-6 bg-white rounded-lg border transition-all hover:shadow-md hover:-translate-y-0.5">
                <div className="bg-primary/10 text-primary w-12 h-12 rounded-lg flex items-center justify-center">
                  <feature.icon className="h-6 w-6" />
                </div>
                <h3 className="mt-4 text-xl font-bold text-slate-900">{feature.name}</h3>
                <p className="mt-2 text-slate-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Pricing Section Placeholder */}
        <section id="pricing" className="py-24 text-center">
          <h2 className="font-display text-4xl md:text-5xl font-bold text-slate-900">Simple, Transparent Pricing</h2>
          <p className="mt-4 text-lg text-slate-600">Get started for free, then choose a plan that fits your growth.</p>
          <div className="mt-12 p-12 bg-slate-50 rounded-2xl max-w-sm mx-auto border shadow-sm transition-all hover:shadow-md">
            <h3 className="text-2xl font-bold">Free Tier</h3>
            <p className="text-5xl font-extrabold mt-4">$0</p>
            <p className="mt-2 text-slate-500">For individuals getting started</p>
            <ul className="mt-6 text-left space-y-2">
              <li className="flex items-center"><ShieldCheck className="h-5 w-5 mr-2 text-green-500"/>100 messages / month</li>
              <li className="flex items-center"><ShieldCheck className="h-5 w-5 mr-2 text-green-500"/>Access to all coaches</li>
            </ul>
            <Link href="/signup" className="mt-8 block w-full bg-primary text-primary-foreground py-3 rounded-lg font-semibold hover:bg-primary/90">
              Start for Free
            </Link>
          </div>
        </section>
      </main>

      {/* Pricing Modal */}
      <PricingModal 
        isOpen={isPricingModalOpen} 
        onClose={() => setIsPricingModalOpen(false)} 
      />
    </div>
  );
};

export default LandingPage;
